import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Upload, Loader2, X, ChevronDown, ChevronUp, Bell, Mail, MessageCircle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Máscaras ──────────────────────────────────────────────────────────────────

const applyTelefoneMask = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};

const applyValorMask = (v) => {
  const digits = v.replace(/\D/g, '');
  if (!digits) return '';
  const num = (parseInt(digits, 10) / 100).toFixed(2);
  return 'R$ ' + num.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseValor = (v) => {
  if (!v) return 0;
  return parseFloat(v.replace('R$ ', '').replace(/\./g, '').replace(',', '.')) || 0;
};

// ── MultiSelect Component ─────────────────────────────────────────────────────

const MultiSelect = ({ options, selected, onChange, placeholder = 'Selecione...' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o.nome.toLowerCase().includes(search.toLowerCase()));
  const toggle = (nome) => {
    if (selected.includes(nome)) onChange(selected.filter(s => s !== nome));
    else onChange([...selected, nome]);
  };
  const remove = (nome, e) => { e.stopPropagation(); onChange(selected.filter(s => s !== nome)); };

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(!open)}
        className="min-h-[44px] w-full px-3 py-2 rounded-xl border border-gray-200 bg-white focus-within:border-primary cursor-pointer flex flex-wrap gap-1 items-center"
      >
        {selected.length === 0 && (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        )}
        {selected.slice(0, 2).map(s => (
          <span key={s} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs px-2 py-0.5 rounded-full border border-red-200">
            {s}
            <button type="button" onClick={(e) => remove(s, e)} className="hover:text-red-900">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {selected.length > 2 && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            +{selected.length - 2} outras
          </span>
        )}
        <span className="ml-auto text-gray-400">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-2 py-1 text-sm outline-none"
              onClick={e => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-sm text-gray-400 p-3 text-center">Nenhum resultado</p>
            )}
            {filtered.map(o => (
              <label
                key={o.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                onClick={() => toggle(o.nome)}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o.nome)}
                  onChange={() => {}}
                  className="accent-red-500"
                />
                {o.nome}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── AutocompleteCombo Component (permite criar novos) ────────────────────────

const AutocompleteCombo = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  id, 
  testId,
  onCreateNew,  // função para criar nova opção
  entityName = 'item' // "seguradora" ou "corretora"
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { 
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => 
    o.nome.toLowerCase().includes(search.toLowerCase())
  );
  
  const label = value || '';
  const searchTrimmed = search.trim();
  
  // Verifica se o termo buscado já existe (case insensitive)
  const exactMatch = options.some(o => 
    o.nome.toLowerCase() === searchTrimmed.toLowerCase()
  );
  
  // Mostra opção de criar apenas se:
  // - Há texto digitado
  // - Não existe correspondência exata
  // - Tem pelo menos 2 caracteres
  const canCreate = searchTrimmed.length >= 2 && !exactMatch;

  const handleSelect = (nome) => {
    onChange(nome);
    setOpen(false);
    setSearch('');
  };

  const handleCreateClick = () => {
    setShowConfirm(true);
  };

  const confirmCreate = async () => {
    if (onCreateNew) {
      const success = await onCreateNew(searchTrimmed);
      if (success) {
        onChange(searchTrimmed);
        setOpen(false);
        setSearch('');
      }
    }
    setShowConfirm(false);
  };

  return (
    <div ref={ref} className="relative">
      <div
        id={id}
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="h-11 w-full px-3 rounded-xl border border-gray-200 bg-white flex items-center justify-between cursor-pointer text-sm"
      >
        <span className={label ? 'text-gray-900' : 'text-gray-400'}>{label || placeholder}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar ou digitar novo..."
              className="w-full px-2 py-1 text-sm outline-none"
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {/* Opção para limpar */}
            <div
              className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-400"
              onClick={() => handleSelect('')}
            >
              {placeholder}
            </div>
            
            {/* Opções existentes filtradas */}
            {filtered.map(o => (
              <div
                key={o.id}
                className={`px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm ${value === o.nome ? 'bg-red-50 text-red-700 font-medium' : ''}`}
                onClick={() => handleSelect(o.nome)}
              >
                {o.nome}
              </div>
            ))}
            
            {/* Opção para criar novo */}
            {canCreate && (
              <div
                className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm border-t border-gray-100 flex items-center gap-2 text-green-700 font-medium"
                onClick={handleCreateClick}
              >
                <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs">+</span>
                Cadastrar "{searchTrimmed}"
              </div>
            )}
            
            {/* Mensagem quando não encontra e termo é curto */}
            {filtered.length === 0 && !canCreate && searchTrimmed.length > 0 && (
              <p className="text-sm text-gray-400 p-3 text-center">
                Digite pelo menos 2 caracteres para cadastrar
              </p>
            )}
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Cadastrar nova {entityName}?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              A {entityName} <strong>"{searchTrimmed}"</strong> não foi encontrada. 
              Deseja cadastrá-la no sistema?
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white"
                onClick={confirmCreate}
              >
                Cadastrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── SearchSelect Component (mantido para compatibilidade) ────────────────────

const SearchSelect = ({ options, value, onChange, placeholder, id, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o.nome.toLowerCase().includes(search.toLowerCase()));
  const label = value || '';

  return (
    <div ref={ref} className="relative">
      <div
        id={id}
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="h-11 w-full px-3 rounded-xl border border-gray-200 bg-white flex items-center justify-between cursor-pointer text-sm"
      >
        <span className={label ? 'text-gray-900' : 'text-gray-400'}>{label || placeholder}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-2 py-1 text-sm outline-none"
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <div
              className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-400"
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
            >
              {placeholder}
            </div>
            {filtered.map(o => (
              <div
                key={o.id}
                className={`px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm ${value === o.nome ? 'bg-red-50 text-red-700 font-medium' : ''}`}
                onClick={() => { onChange(o.nome); setOpen(false); setSearch(''); }}
              >
                {o.nome}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── NovoLembrete ──────────────────────────────────────────────────────────────

const NovoLembrete = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [isCorretor, setIsCorretor] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [user, setUser] = useState(null);

  const [tiposSeguro, setTiposSeguro] = useState([]);
  const [seguradoras, setSeguradoras] = useState([]);
  const [corretoras, setCorretoras] = useState([]);
  const [servicosDisponiveis, setServicosDisponiveis] = useState([]);

  const [formData, setFormData] = useState({
    cliente_nome: '',
    cliente_email: '',
    cliente_telefone: '',
    tipo_servico: '',
    tipos_seguro_selecionados: [], // multiselect
    data_vigencia: new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }).split('/').reverse().join('-'), // 🔥 Data atual em São Paulo
    data_renovacao: '',
    recorrencia: 'anual',
    observacoes: '',
    seguradora: '',
    corretora: '',
    numero_apolice: '',
    valor: '',
    total_vidas: '',
    status_lembrete: 'contatado',
    // Toggles de notificação (para Corretor)
    notificar_cliente_email: true,    // Se true: cliente recebe | Se false: corretora recebe
    notificar_cliente_whatsapp: true  // Se true: cliente recebe | Se false: corretora recebe
  });

  // 🔥 NOVA: Flag para saber se data de renovação foi editada manualmente
  const [dataRenovacaoManual, setDataRenovacaoManual] = useState(false);

  // 🔥 NOVA: Função para calcular data de renovação baseado na recorrência
  const calcularDataRenovacao = (dataVigencia, recorrencia) => {
    if (!dataVigencia) return '';
    
    const data = new Date(dataVigencia + 'T00:00:00');
    
    switch (recorrencia) {
      case 'semanal':
        data.setDate(data.getDate() + 7);
        break;
      case 'quinzenal':
        data.setDate(data.getDate() + 15);
        break;
      case 'mensal':
        data.setMonth(data.getMonth() + 1);
        break;
      case 'semestral':
        data.setMonth(data.getMonth() + 6);
        break;
      case 'anual':
      default:
        data.setFullYear(data.getFullYear() + 1);
        break;
    }
    
    return data.toISOString().split('T')[0];
  };

  // 🔥 NOVO: useEffect para calcular data de renovação automaticamente
  useEffect(() => {
    // Só calcula automaticamente se não foi editado manualmente
    if (!dataRenovacaoManual && formData.data_vigencia && formData.recorrencia) {
      const novaDataRenovacao = calcularDataRenovacao(formData.data_vigencia, formData.recorrencia);
      setFormData(prev => ({ ...prev, data_renovacao: novaDataRenovacao }));
    }
  }, [formData.data_vigencia, formData.recorrencia, dataRenovacaoManual]);

  useEffect(() => {
    const userStr = localStorage.getItem('lembrou_user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
        setIsCorretor(userData.tipo_profissao === 'Corretor de Seguros');
        
        // Buscar serviços da profissão do usuário
        if (userData.tipo_profissao && userData.tipo_profissao !== 'Corretor de Seguros') {
          const token = localStorage.getItem('lembrou_token');
          axios.get(`${API}/servicos/por-profissao-nome/${encodeURIComponent(userData.tipo_profissao)}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then(res => {
              if (res.data && res.data.length > 0) {
                setServicosDisponiveis(res.data);
              } else {
                // Fallback caso não tenha serviços cadastrados
                setServicosDisponiveis([{ id: 0, nome: 'Outro' }]);
              }
            })
            .catch(() => {
              setServicosDisponiveis([{ id: 0, nome: 'Outro' }]);
            });
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('lembrou_token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    Promise.all([
      axios.get(`${API}/tipos-seguro`, config),
      axios.get(`${API}/seguradoras`, config),
      axios.get(`${API}/corretoras`, config)
    ]).then(([ts, seg, cor]) => {
      setTiposSeguro(ts.data || []);
      setSeguradoras(seg.data || []);
      setCorretoras(cor.data || []);
    }).catch(() => {});

    if (isEdit) {
      const token2 = localStorage.getItem('lembrou_token');
      axios.get(`${API}/lembretes`, { headers: { Authorization: `Bearer ${token2}` } })
        .then(res => {
          const lembrete = res.data.find(l => l.id === id);
          if (!lembrete) { toast.error('Lembrete não encontrado'); navigate('/dashboard'); return; }
          // tipos_seguro pode ser string separada por vírgula
          const tiposSelecionados = lembrete.tipo_servico
            ? lembrete.tipo_servico.split(',').map(s => s.trim()).filter(Boolean)
            : [];
          setFormData({
            cliente_nome: lembrete.cliente_nome || '',
            cliente_email: lembrete.cliente_email || '',
            cliente_telefone: lembrete.cliente_telefone || '',
            tipo_servico: lembrete.tipo_servico || '',
            tipos_seguro_selecionados: tiposSelecionados,
            data_vigencia: lembrete.data_vigencia || '',
            data_renovacao: lembrete.data_renovacao || '',
            recorrencia: lembrete.recorrencia || 'anual',
            observacoes: lembrete.observacoes || '',
            seguradora: lembrete.seguradora || '',
            corretora: lembrete.corretora || '',
            numero_apolice: lembrete.numero_apolice || '',
            valor: lembrete.valor ? applyValorMask(String(Math.round(lembrete.valor * 100))) : '',
            total_vidas: lembrete.total_vidas || '',
            status_lembrete: lembrete.status_lembrete || 'contatado'
          });
          // Marca que a data de renovação existente foi definida (não calcular automaticamente)
          setDataRenovacaoManual(true);
        })
        .catch(() => toast.error('Erro ao carregar lembrete'));
    }
  }, [isEdit, id, navigate]);

  const handlePDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPDF(true);
    try {
      const token = localStorage.getItem('lembrou_token');
      const fd = new FormData();
      fd.append('file', file);
      const { data: d } = await axios.post(`${API}/ocr/extract`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const tiposSelecionados = d.tipo_servico
        ? d.tipo_servico.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      setFormData(prev => ({
        ...prev,
        cliente_nome: d.cliente_nome || prev.cliente_nome,
        cliente_email: d.email_segurado || prev.cliente_email,
        cliente_telefone: d.telefone_segurado ? applyTelefoneMask(d.telefone_segurado) : prev.cliente_telefone,
        tipo_servico: d.tipo_servico || prev.tipo_servico,
        tipos_seguro_selecionados: tiposSelecionados.length ? tiposSelecionados : prev.tipos_seguro_selecionados,
        seguradora: d.seguradora || prev.seguradora,
        corretora: d.corretora || d.corretor_nome || prev.corretora,
        numero_apolice: d.numero_apolice || prev.numero_apolice,
        valor: d.valor ? applyValorMask(String(Math.round(d.valor * 100))) : prev.valor,
        data_vigencia: d.data_vigencia || prev.data_vigencia,
        data_renovacao: d.data_renovacao || prev.data_renovacao,
      }));
      toast.success('Dados extraídos com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar PDF');
    } finally {
      setUploadingPDF(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Apenas arquivos PDF são aceitos');
      return;
    }
    // Reuse the same upload logic
    const fakeEvent = { target: { files: [file] } };
    await handlePDFUpload(fakeEvent);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  // ── Criar nova Seguradora ──
  const criarSeguradora = async (nome) => {
    try {
      const token = localStorage.getItem('lembrou_token');
      const response = await axios.post(
        `${API}/seguradoras`,
        { nome },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Adiciona à lista local
      setSeguradoras(prev => [...prev, response.data]);
      toast.success(`Seguradora "${nome}" cadastrada com sucesso!`);
      return true;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao cadastrar seguradora';
      toast.error(msg);
      return false;
    }
  };

  // ── Criar nova Corretora ──
  const criarCorretora = async (nome) => {
    try {
      const token = localStorage.getItem('lembrou_token');
      const response = await axios.post(
        `${API}/corretoras`,
        { nome },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Adiciona à lista local
      setCorretoras(prev => [...prev, response.data]);
      toast.success(`Corretora "${nome}" cadastrada com sucesso!`);
      return true;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao cadastrar corretora';
      toast.error(msg);
      return false;
    }
  };

  // ── Criar novo Serviço ──
  const criarServico = async (nome) => {
    if (!user?.tipo_profissao) {
      toast.error('Profissão não identificada');
      return false;
    }
    try {
      const token = localStorage.getItem('lembrou_token');
      const response = await axios.post(
        `${API}/servicos`,
        { nome, profissao_nome: user.tipo_profissao },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Adiciona à lista local
      setServicosDisponiveis(prev => [...prev, response.data]);
      toast.success(`Serviço "${nome}" cadastrado com sucesso!`);
      return true;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao cadastrar serviço';
      toast.error(msg);
      return false;
    }
  };

  const validarFormulario = () => {
    if (!formData.cliente_nome.trim()) {
      toast.error('Informe o nome do cliente'); return false;
    }
    if (formData.cliente_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cliente_email)) {
      toast.error('E-mail do cliente inválido'); return false;
    }
    if (formData.cliente_telefone) {
      const digits = formData.cliente_telefone.replace(/\D/g, '');
      if (digits.length < 10) { toast.error('WhatsApp inválido — informe com DDD'); return false; }
    }
    if (isCorretor) {
      if (formData.tipos_seguro_selecionados.length === 0) {
        toast.error('Selecione ao menos um tipo de seguro'); return false;
      }
      if (!formData.data_vigencia) { toast.error('Informe a data de vigência'); return false; }
      if (!formData.data_renovacao) { toast.error('Informe a data de renovação'); return false; }
      if (formData.data_renovacao < formData.data_vigencia) {
        toast.error('Data de renovação deve ser após a data de vigência'); return false;
      }
      const temVida = formData.tipos_seguro_selecionados.some(t => t.toLowerCase().includes('vida'));
      if (temVida && (!formData.total_vidas || parseInt(formData.total_vidas) < 1)) {
        toast.error('Informe o total de vidas para seguro Vida'); return false;
      }
    } else {
      if (!formData.tipo_servico.trim()) { toast.error('Informe o tipo de serviço'); return false; }
      if (!formData.data_vigencia) { toast.error('Informe a data de vigência'); return false; }
      if (!formData.data_renovacao) { toast.error('Informe a data de renovação'); return false; }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('lembrou_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Para Corretor: une os tipos selecionados em string separada por vírgula
      const tipoServicoFinal = isCorretor
        ? formData.tipos_seguro_selecionados.join(', ')
        : formData.tipo_servico;

      const payload = {
        ...formData,
        tipo_servico: tipoServicoFinal,
        valor: parseValor(formData.valor),
        total_vidas: parseInt(formData.total_vidas) || 0
      };
      delete payload.tipos_seguro_selecionados;

      if (isEdit) {
        await axios.put(`${API}/lembretes/${id}`, payload, config);
        toast.success('Lembrete atualizado!');
      } else {
        const response = await axios.post(`${API}/lembretes`, payload, config);
        setQrCode(response.data.qr_code_data);
        toast.success('Lembrete criado com sucesso!');
      }
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erro ao salvar lembrete');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectClass = "w-full h-11 px-3 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-2 focus:ring-red-100 outline-none text-sm";
  const temVida = formData.tipos_seguro_selecionados.some(t => t.toLowerCase().includes('vida'));

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button data-testid="back-button" onClick={() => navigate('/dashboard')} variant="ghost" className="rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          {isEdit ? 'Editar Lembrete' : 'Novo Lembrete'}
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className={qrCode && !isEdit ? "lg:col-span-2" : "lg:col-span-3"}>

            {/* PDF Upload - APENAS para Corretores de Seguros */}
            {!isEdit && isCorretor && (
              <Card className="p-6 rounded-2xl border shadow-sm mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  📄 Importar Apólice PDF
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Extraia dados automaticamente da apólice
                </p>
                <div
                  className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-red-400 bg-red-100'
                      : 'border-red-200 bg-red-50/50 hover:bg-red-50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-red-500' : 'text-red-400'}`} />
                  <p className="text-sm text-gray-600 mb-1">
                    {isDragging ? 'Solte o arquivo aqui!' : 'Arraste um PDF ou clique para selecionar'}
                  </p>
                  <p className="text-xs text-gray-400 mb-4">Suporta: Porto, Tokio Marine, Allianz, AXA, Icatu, Mapfre</p>
                  <input data-testid="pdf-upload-input" type="file" accept=".pdf" onChange={handlePDFUpload} disabled={uploadingPDF} className="hidden" id="pdf-upload" />
                  <label htmlFor="pdf-upload">
                    <Button type="button" data-testid="pdf-upload-button" disabled={uploadingPDF} className="rounded-2xl bg-primary hover:bg-primary-hover" onClick={() => document.getElementById('pdf-upload').click()}>
                      {uploadingPDF ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processando...</>) : 'Selecionar PDF'}
                    </Button>
                  </label>
                </div>
              </Card>
            )}

            {/* Form */}
            <Card className="p-6 rounded-2xl border shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {isCorretor ? 'Dados do Cliente / Seguro' : 'Informações do Lembrete'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Dados do cliente */}
                <div>
                  <Label htmlFor="cliente_nome">Nome do Cliente</Label>
                  <Input
                    id="cliente_nome"
                    data-testid="cliente-nome-input"
                    value={formData.cliente_nome}
                    onChange={(e) => {
                      if (/\d/.test(e.target.value)) return;
                      setFormData({ ...formData, cliente_nome: e.target.value });
                    }}
                    required
                    className="rounded-xl h-11"
                    placeholder="Nome completo"
                  />
                </div>

                <div>
                  <Label htmlFor="cliente_email">Email do Cliente</Label>
                  <Input
                    id="cliente_email"
                    data-testid="cliente-email-input"
                    type="email"
                    placeholder="cliente@email.com"
                    value={formData.cliente_email}
                    onChange={(e) => setFormData({ ...formData, cliente_email: e.target.value })}
                    className="rounded-xl h-11"
                  />
                </div>

                <div>
                  <Label htmlFor="cliente_telefone">
                    WhatsApp do Cliente
                    <span className="ml-2 text-xs font-normal text-gray-400">(para notificações)</span>
                  </Label>
                  <Input
                    id="cliente_telefone"
                    data-testid="cliente-telefone-input"
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={formData.cliente_telefone}
                    onChange={(e) => setFormData({ ...formData, cliente_telefone: applyTelefoneMask(e.target.value) })}
                    className="rounded-xl h-11"
                  />
                </div>

                {/* ===== TOGGLES DE NOTIFICAÇÃO - APENAS CORRETOR ===== */}
                {isCorretor && (
                  <div className="col-span-1 md:col-span-2 bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Bell className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-gray-800 text-sm">Quem recebe as notificações?</span>
                      <div className="group relative">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          Escolha se o cliente ou a corretora recebe os lembretes
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Toggle Email */}
                      <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">Email</p>
                            <p className="text-xs text-gray-500">
                              {formData.notificar_cliente_email ? 'Cliente recebe' : 'Corretora recebe'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, notificar_cliente_email: !formData.notificar_cliente_email })}
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            formData.notificar_cliente_email ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            formData.notificar_cliente_email ? 'translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>

                      {/* Toggle WhatsApp */}
                      <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">WhatsApp</p>
                            <p className="text-xs text-gray-500">
                              {formData.notificar_cliente_whatsapp ? 'Cliente recebe' : 'Corretora recebe'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, notificar_cliente_whatsapp: !formData.notificar_cliente_whatsapp })}
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            formData.notificar_cliente_whatsapp ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            formData.notificar_cliente_whatsapp ? 'translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-xs text-blue-600 mt-2">
                      💡 Toggle ligado = cliente recebe | Toggle desligado = corretora recebe
                    </p>
                  </div>
                )}

                {/* ===== CAMPOS CORRETOR ===== */}
                {isCorretor ? (
                  <>
                    <div>
                      <Label>Tipo de Seguro <span className="text-red-500">*</span></Label>
                      <MultiSelect
                        options={tiposSeguro}
                        selected={formData.tipos_seguro_selecionados}
                        onChange={(val) => setFormData({ ...formData, tipos_seguro_selecionados: val })}
                        placeholder="Selecione os tipos de seguro..."
                      />
                    </div>

                    {temVida && (
                      <div>
                        <Label htmlFor="total_vidas">Total de Vidas <span className="text-red-500">*</span></Label>
                        <Input
                          id="total_vidas"
                          data-testid="total-vidas-input"
                          type="number"
                          min="1"
                          placeholder="Quantidade de vidas"
                          value={formData.total_vidas}
                          onChange={(e) => setFormData({ ...formData, total_vidas: e.target.value.replace(/\D/g, '') })}
                          className="rounded-xl h-11"
                        />
                      </div>
                    )}

                    <div>
                      <Label>Seguradora</Label>
                      <AutocompleteCombo
                        id="seguradora"
                        testId="seguradora-select"
                        options={seguradoras}
                        value={formData.seguradora}
                        onChange={(val) => setFormData({ ...formData, seguradora: val })}
                        placeholder="Selecione a seguradora"
                        onCreateNew={criarSeguradora}
                        entityName="seguradora"
                      />
                    </div>

                    <div>
                      <Label>Corretora</Label>
                      <AutocompleteCombo
                        id="corretora"
                        testId="corretora-select"
                        options={corretoras}
                        value={formData.corretora}
                        onChange={(val) => setFormData({ ...formData, corretora: val })}
                        placeholder="Selecione a corretora"
                        onCreateNew={criarCorretora}
                        entityName="corretora"
                      />
                    </div>

                    <div>
                      <Label htmlFor="numero_apolice">Número da Apólice</Label>
                      <Input
                        id="numero_apolice"
                        data-testid="numero-apolice-input"
                        placeholder="Ex: APL-2026-001"
                        value={formData.numero_apolice}
                        onChange={(e) => setFormData({ ...formData, numero_apolice: e.target.value.toUpperCase() })}
                        className="rounded-xl h-11 font-mono tracking-wide"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="valor">Valor do Prêmio</Label>
                        <Input
                          id="valor"
                          data-testid="valor-input"
                          placeholder="R$ 0,00"
                          value={formData.valor}
                          onChange={(e) => setFormData({ ...formData, valor: applyValorMask(e.target.value) })}
                          className="rounded-xl h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="status_lembrete">Status</Label>
                        <select
                          id="status_lembrete"
                          data-testid="status-lembrete-select"
                          value={formData.status_lembrete}
                          onChange={(e) => setFormData({ ...formData, status_lembrete: e.target.value })}
                          className={selectClass}
                        >
                          <option value="contatado">Contatado</option>
                          <option value="em andamento">Em Andamento</option>
                          <option value="renovado">Renovado</option>
                          <option value="nao renovado">Não Renovado</option>
                        </select>
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <Label htmlFor="tipo_servico">Tipo de Serviço <span className="text-red-500">*</span></Label>
                    <AutocompleteCombo
                      id="tipo_servico"
                      testId="tipo-servico-select"
                      options={servicosDisponiveis}
                      value={formData.tipo_servico}
                      onChange={(val) => setFormData({ ...formData, tipo_servico: val })}
                      placeholder="Selecione ou digite o tipo de serviço..."
                      onCreateNew={criarServico}
                      entityName="serviço"
                    />
                  </div>
                )}

                {/* Recorrência - AGORA VEM PRIMEIRO */}
                <div>
                  <Label htmlFor="recorrencia">Recorrência <span className="text-red-500">*</span></Label>
                  <select
                    id="recorrencia"
                    data-testid="recorrencia-select"
                    value={formData.recorrencia}
                    onChange={(e) => {
                      setFormData({ ...formData, recorrencia: e.target.value });
                      // Reseta flag manual quando muda recorrência
                      setDataRenovacaoManual(false);
                    }}
                    className={selectClass}
                  >
                    <option value="semanal">Semanal</option>
                    <option value="quinzenal">Quinzenal</option>
                    <option value="mensal">Mensal</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>

                {/* Datas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="data_vigencia">Data de Vigência <span className="text-red-500">*</span></Label>
                    <Input
                      id="data_vigencia"
                      data-testid="data-vigencia-input"
                      type="date"
                      value={formData.data_vigencia}
                      onChange={(e) => {
                        setFormData({ ...formData, data_vigencia: e.target.value });
                        // Reseta flag manual quando muda vigência
                        setDataRenovacaoManual(false);
                      }}
                      required
                      className="rounded-xl h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_renovacao">
                      Data de Renovação <span className="text-red-500">*</span>
                      {!dataRenovacaoManual && formData.data_renovacao && (
                        <span className="ml-2 text-xs text-gray-400">(calculada automaticamente)</span>
                      )}
                    </Label>
                    <Input
                      id="data_renovacao"
                      data-testid="data-renovacao-input"
                      type="date"
                      value={formData.data_renovacao}
                      onChange={(e) => {
                        setFormData({ ...formData, data_renovacao: e.target.value });
                        // Marca que foi editado manualmente
                        setDataRenovacaoManual(true);
                      }}
                      required
                      className="rounded-xl h-11"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    data-testid="observacoes-textarea"
                    placeholder="Anotações adicionais..."
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    className="rounded-xl min-h-24"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="submit-lembrete-button"
                  disabled={loading}
                  className="w-full rounded-2xl h-11 bg-primary hover:bg-primary-hover shadow-md"
                >
                  {loading ? 'Salvando...' : (isEdit ? 'Atualizar Lembrete' : 'Criar Lembrete')}
                </Button>
              </form>
            </Card>
          </div>

          {/* QR Code Preview */}
          {qrCode && !isEdit && (
            <div className="lg:col-span-1">
              <Card className="p-6 rounded-2xl border shadow-sm sticky top-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">QR Code Gerado</h2>
                <div className="bg-white p-4 rounded-xl border-2 border-gray-100 flex items-center justify-center">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48" data-testid="qr-code-image" />
                </div>
                <p className="text-xs text-gray-500 text-center mt-4">Compartilhe este QR Code com seu cliente</p>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NovoLembrete;
